import { supabase } from '../supabase';
import { toast } from 'sonner';

let pendingConnectionCheck = null;

/**
 * Health check to ensure session is valid BEFORE a critical operation.
 * Uses a singleton promise to de-duplicate concurrent checks.
 */
export async function verifyConnection() {
  // 1. If a check is already in progress, wait for it
  if (pendingConnectionCheck) {
    return pendingConnectionCheck;
  }

  // 2. Start a new check
  pendingConnectionCheck = (async () => {
    const startTime = Date.now();
    console.group(`--- [DB] Connection Shield: Pre-flight ---`);
    try {
      const { data: { session }, error } = await Promise.race([
        supabase.auth.getSession(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Auth Timeout')), 20000))
      ]);

      if (error) throw error;
      if (!session) throw new Error('No active session found. Please login again.');

      const duration = Date.now() - startTime;
      console.log(`Status: Connected (${duration}ms)`);
      return { ok: true, session };
    } catch (err) {
      console.error('Status: Disconnected', err.message);
      return { ok: false, error: err.message };
    } finally {
      console.groupEnd();
      pendingConnectionCheck = null; // Always clear when done
    }
  })();

  return pendingConnectionCheck;
}




/**
 * Wrapper for database requests with built-in timeout and logging.
 */
export async function safeQuery(promise, options = {}) {
  const { 
    timeout = 15000, 
    label = 'Request',
    showToast = true
  } = options;

  const startTime = Date.now();
  console.group(`--- [DB Query] ${label} ---`);
  
  try {
    // 1. Verify session first
    const { ok, error: authError } = await verifyConnection();
    if (!ok) {
      if (showToast) toast.error(`Connection Error: ${authError}`);
      throw new Error(authError);
    }


    // 2. Race the query against the timeout
    const result = await Promise.race([
      promise,
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timed out (15s)')), timeout)
      )
    ]);

    const duration = Date.now() - startTime;
    console.log(`Result: Success (${duration}ms)`);
    
    // Supabase specific error check (if result is a supabase response)
    if (result && result.error) {
      console.error(`DB Error: ${result.error.message}`);
      if (showToast) toast.error(`Database Error: ${result.error.message}`);
      return result;
    }

    return result;
  } catch (err) {
    const duration = Date.now() - startTime;
    console.error(`Result: Failed after ${duration}ms`);
    console.error(`Error details:`, err);
    
    if (showToast) {
      if (err.message.includes('timed out')) {
        toast.error('The request timed out. This usually happens due to a network glitch. Please try clicking save again.');
      } else {
        toast.error(`Error: ${err.message}`);
      }
    }
    
    throw err;
  } finally {
    console.groupEnd();
  }
}
